/**
 * ActivityReport
 *
 * A temporary report component to display user activity metrics.
 * Shows time spent by users across different areas of the app.
 */

import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BarChartIcon from '@mui/icons-material/BarChart'
import BusinessIcon from '@mui/icons-material/Business'
import CampaignIcon from '@mui/icons-material/Campaign'
import FilterListIcon from '@mui/icons-material/FilterList'
import PersonIcon from '@mui/icons-material/Person'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import type { EntityVisitor, GoogleAdsAccountBreakdown, GoogleAdsTeamSummaryUser } from 'api/aws/analytics'
import {
  getTimeRangePreset,
  useActivitySummary,
  useGoogleAdsByAccount,
  useGoogleAdsTeamSummary,
  useOrganizationTime,
} from 'api/aws/analytics'
import { useGoogleAdsAccounts } from 'api/aws/analytics/useGoogleAdsAccounts'
import { useUsers } from 'api/aws/useUsers'
import { useSearchData } from 'api/search/useSearchData'
import SelectActiveUser from 'components/Dashboard/SelectActiveUser'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useCallback, useMemo, useState } from 'react'
import { ScrollableContainer } from 'styles/elements/styleElements'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { UserOption } from 'types'
import UnlinkedAccount from './UnlinkedAccount'

/** User info lookup type */
interface UserInfo {
  id: string
  name: string
  photoUrl?: string
}

/** Props for VisitorAvatars component */
interface VisitorAvatarsProps {
  visitors: EntityVisitor[]
  userLookup: Record<string, UserInfo>
  max?: number
  size?: 'xs' | 'sm'
  fallbackCount?: number
}

/**
 * Renders a row of visitor avatars using EntityAvatar
 * Shows photos when available, initials when not
 */
const VisitorAvatars: React.FC<VisitorAvatarsProps> = ({
  visitors,
  userLookup,
  max = 4,
  size = 'sm',
  fallbackCount,
}) => {
  if (!visitors || visitors.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {fallbackCount ?? 0}
      </Typography>
    )
  }

  const displayVisitors = visitors.slice(0, max)
  const remainingCount = visitors.length - max

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {displayVisitors.map(visitor => {
        const userInfo = userLookup[visitor.userId]
        return (
          <Tooltip key={visitor.userId} title={userInfo?.name || visitor.userName} arrow>
            <span>
              <EntityAvatar
                name={userInfo?.name || visitor.userName}
                photoUrl={userInfo?.photoUrl}
                size={size}
                showName={false}
                userId={visitor.userId}
                enableNav={true}
              />
            </span>
          </Tooltip>
        )
      })}
      {remainingCount > 0 && (
        <Tooltip title={`+${remainingCount} more`} arrow>
          <Box
            sx={{
              width: size === 'xs' ? 20 : 28,
              height: size === 'xs' ? 20 : 28,
              borderRadius: '50%',
              bgcolor: 'action.selected',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: size === 'xs' ? '0.6rem' : '0.7rem',
              fontWeight: 600,
              color: 'text.secondary',
            }}
          >
            +{remainingCount}
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}

type TimeRange = 'today' | 'yesterday' | 'week' | 'month'

const ActivityReport: React.FC = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const [timeRange, setTimeRange] = useState<TimeRange>('today')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  // Memoize the time range to prevent unnecessary refetches
  const range = useMemo(() => getTimeRangePreset(timeRange), [timeRange])

  // Get search data for org-deal mappings
  const { organizations: searchOrganizations, deals: searchDeals } = useSearchData()

  // Get all users for photo lookup
  const { users } = useUsers(true, ['activityReport', 'users'])

  // Build user lookup map with photos
  const userLookup = useMemo(() => {
    const lookup: Record<string, UserInfo> = {}
    if (users && Array.isArray(users)) {
      for (const user of users) {
        const id = user?.id || user?.user_uuid
        if (id) {
          lookup[id] = {
            id,
            name: user?.real_name || user?.name || id,
            photoUrl: user?.image_512 || user?.profile?.image_512 || user?.profile?.image_192,
          }
        }
      }
    }
    return lookup
  }, [users])

  // Build org-deal mappings from search data
  const orgDealMappings = useMemo(() => {
    if (!searchOrganizations?.length || !searchDeals?.length) return undefined

    const mappings: Record<string, string[]> = {}

    // Initialize all orgs
    for (const org of searchOrganizations) {
      const orgId = org.PK || `ORG_${org.org_uuid}`
      mappings[orgId] = []
    }

    // Map deals to their organizations
    for (const deal of searchDeals) {
      const dealId = deal.PK || `DEAL_${deal.deal_uuid}`
      const dealOrgs = deal.organizations || []

      for (const orgRef of dealOrgs) {
        // orgRef could be "ORG_xxx" or just the uuid
        const orgId = orgRef.startsWith('ORG_') ? orgRef : `ORG_${orgRef}`
        if (mappings[orgId]) {
          mappings[orgId].push(dealId)
        }
      }
    }

    return mappings
  }, [searchOrganizations, searchDeals])

  // Create a lookup map for org names
  const orgNameLookup = useMemo(() => {
    if (!searchOrganizations?.length) return {}
    const lookup: Record<string, string> = {}
    for (const org of searchOrganizations) {
      const orgId = org.PK || `ORG_${org.org_uuid}`
      lookup[orgId] = org.name || org.organization_name || orgId
    }
    return lookup
  }, [searchOrganizations])

  const { data, isLoading, error } = useActivitySummary({
    startTime: range.startTime,
    endTime: range.endTime,
  })

  // Fetch organization time data
  const { data: orgTimeData, isLoading: orgTimeLoading } = useOrganizationTime(orgDealMappings, {
    startTime: range.startTime,
    endTime: range.endTime,
    enabled: !!orgDealMappings,
  })

  // Get all user IDs for Google Ads team summary
  const allUserIds = useMemo(() => {
    if (!users || !Array.isArray(users)) return []
    return users.map(u => u?.id).filter(Boolean)
  }, [users])

  // Convert time range to ISO date strings for Google Ads API
  const googleAdsDateRange = useMemo(() => {
    return {
      startDate: new Date(range.startTime).toISOString(),
      endDate: new Date(range.endTime).toISOString(),
    }
  }, [range])

  // Fetch Google Ads team summary
  const { data: googleAdsData, isLoading: googleAdsLoading } = useGoogleAdsTeamSummary(allUserIds, {
    startDate: googleAdsDateRange.startDate,
    endDate: googleAdsDateRange.endDate,
    enabled: allUserIds.length > 0,
  })
  // Build mapping of Google Ads Account IDs to Organizations
  // Organizations can have both googleAdsId (display ID from MCC) and googleAdsCustomerId (URL customer ID)
  const accountOrgMapping = useMemo(() => {
    if (!searchOrganizations?.length) return {}
    const mapping: Record<string, { orgId: string; orgName: string }> = {}
    for (const org of searchOrganizations) {
      const orgInfo = {
        orgId: org.PK || `ORG_${org.org_uuid}`,
        orgName: org.name || org.organization_name || 'Unknown',
      }

      // Map the display ID (googleAdsId - shown in MCC account list)
      const googleAdsId = org.googleAdsId
      if (googleAdsId) {
        const normalizedId = String(googleAdsId).replace(/[-\s]/g, '')
        mapping[normalizedId] = orgInfo
      }

      // Also map the Customer ID (googleAdsCustomerId - extracted from URLs by extension)
      const googleAdsCustomerId = org.googleAdsCustomerId
      if (googleAdsCustomerId) {
        const normalizedCustomerId = String(googleAdsCustomerId).replace(/[-\s]/g, '')
        mapping[normalizedCustomerId] = orgInfo
      }
    }
    return mapping
  }, [searchOrganizations])

  // Fetch Google Ads account names from API (for unlinked accounts)
  // NOTE: This hook must be called before the useMemos that depend on googleAdsAccountsById
  const {
    accountsById: googleAdsAccountsById,
    isLoading: googleAdsAccountsLoading,
    error: googleAdsError,
    isAuthError: googleAdsAuthError,
    errorMessage: googleAdsErrorMessage,
    errorDetails: googleAdsErrorDetails,
    startReAuth: startGoogleAdsReAuth,
    authWindowOpen: googleAdsAuthWindowOpen,
  } = useGoogleAdsAccounts({
    enabled: true,
  })

  // Build name-based matching as fallback (matches Google Ads account names to org names)
  // This runs after explicit ID matching to auto-link accounts by name
  const nameBasedOrgMapping = useMemo(() => {
    if (!searchOrganizations?.length || !googleAdsAccountsById) return {}

    const mapping: Record<string, { orgId: string; orgName: string; matchedBy: 'name' }> = {}

    // For each Google Ads account, try to find a matching org by name
    for (const [customerId, account] of Object.entries(googleAdsAccountsById)) {
      // Skip if already explicitly mapped
      if (accountOrgMapping[customerId]) continue

      const accountName = account.name?.toLowerCase() || ''
      if (!accountName) continue

      // Try to find an org whose name is contained in the Google Ads account name
      for (const org of searchOrganizations) {
        const orgName = (org.name || org.organization_name || '').toLowerCase().trim()
        if (!orgName || orgName.length < 3) continue // Skip very short names

        // Check if org name is contained in Google Ads account name
        // e.g., "The Hudson" matches "Adam America - The Hudson"
        if (accountName.includes(orgName)) {
          mapping[customerId] = {
            orgId: org.PK || `ORG_${org.org_uuid}`,
            orgName: org.name || org.organization_name || 'Unknown',
            matchedBy: 'name',
          }
          break // Use first match
        }
      }
    }

    return mapping
  }, [searchOrganizations, googleAdsAccountsById, accountOrgMapping])

  // Combined mapping: explicit IDs first, then name-based fallback
  const combinedOrgMapping = useMemo(() => {
    return { ...nameBasedOrgMapping, ...accountOrgMapping } // accountOrgMapping takes priority
  }, [accountOrgMapping, nameBasedOrgMapping])

  // Fetch Google Ads time by account with org mapping (includes name-based fallback)
  const { data: googleAdsByAccountData, isLoading: googleAdsByAccountLoading } = useGoogleAdsByAccount(
    allUserIds,
    combinedOrgMapping,
    {
      startDate: googleAdsDateRange.startDate,
      endDate: googleAdsDateRange.endDate,
      enabled: allUserIds.length > 0,
    }
  )

  // Format minutes to hours and minutes string
  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  // Format duration helper (used in filtering)
  const formatDuration = useCallback((ms: number): string => {
    if (ms < 60000) return '0m'
    const minutes = Math.floor(ms / 60000)
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }
    return `${minutes}m`
  }, [])

  // Create user options for the filter (sorted by name)
  const userOptions: UserOption[] = useMemo(() => {
    if (!data) return []
    const options = data.userBreakdown
      .map(u => {
        const user = userLookup[u.userId]
        return {
          id: u.userId,
          user_uuid: u.userId,
          label: u.userName,
          name: u.userName,
          first_name: '',
          last_name: '',
          firstName: '',
          lastName: '',
          avatar: user?.photoUrl || null,
        } as UserOption
      })
      .sort((a, b) => a.label.localeCompare(b.label))
    return options
  }, [data, userLookup])

  // Filter data by selected user
  const filteredData = useMemo(() => {
    if (!data) {
      // Return empty structure if no data
      return {
        summary: {
          totalDuration: 0,
          totalDurationFormatted: '0m',
          totalVisits: 0,
          uniqueUsers: 0,
          averageDurationPerVisit: 0,
          averageDurationPerVisitFormatted: '0m',
        },
        areaBreakdown: [],
        userBreakdown: [],
        topEntities: [],
      }
    }

    const { summary, areaBreakdown, userBreakdown, topEntities } = data

    if (!selectedUserId) {
      // No filter - return all data
      return {
        summary,
        areaBreakdown,
        userBreakdown,
        topEntities,
      }
    }

    // Filter for selected user only
    const selectedUserData = userBreakdown.find(u => u.userId === selectedUserId)

    if (!selectedUserData) {
      // User not found - return empty data
      return {
        summary: {
          totalDuration: 0,
          totalDurationFormatted: '0m',
          totalVisits: 0,
          uniqueUsers: 0,
          averageDurationPerVisit: 0,
          averageDurationPerVisitFormatted: '0m',
        },
        areaBreakdown: [],
        userBreakdown: [],
        topEntities: [],
      }
    }

    // Filter area breakdown to only show areas this user has visited
    // We need to check the raw data to see which areas the user visited
    const userAreaBreakdown = areaBreakdown.filter(area => {
      // Check if any entity in this area has this user as a visitor
      return topEntities.some(
        entity => entity.area === area.area && entity.visitors.some(visitor => visitor.userId === selectedUserId)
      )
    })

    // Filter top entities for this user
    const userTopEntities = topEntities.filter(entity =>
      entity.visitors.some(visitor => visitor.userId === selectedUserId)
    )

    // Calculate average duration per visit
    const avgDuration = selectedUserData.visits > 0 ? selectedUserData.duration / selectedUserData.visits : 0

    return {
      summary: {
        totalDuration: selectedUserData.duration,
        totalDurationFormatted: selectedUserData.durationFormatted,
        totalVisits: selectedUserData.visits,
        uniqueUsers: 1,
        averageDurationPerVisit: avgDuration,
        averageDurationPerVisitFormatted: formatDuration(avgDuration),
      },
      areaBreakdown: userAreaBreakdown,
      userBreakdown: [selectedUserData],
      topEntities: userTopEntities,
    }
  }, [selectedUserId, data, formatDuration])

  // Filter organization time data by selected user
  const filteredOrgTimeData = useMemo(() => {
    if (!orgTimeData?.organizationTime) return null

    if (!selectedUserId) {
      // No filter - return all data
      return orgTimeData.organizationTime
    }

    // Filter to only show organizations where the selected user is a visitor
    return orgTimeData.organizationTime.filter(org =>
      org.visitors?.some((visitor: EntityVisitor) => visitor.userId === selectedUserId)
    )
  }, [selectedUserId, orgTimeData])

  // Filter Google Ads data by selected user
  const filteredGoogleAdsData = useMemo(() => {
    if (!googleAdsData) return null

    if (!selectedUserId) {
      // No filter - return all data
      return googleAdsData
    }

    // Filter users array to only the selected user
    const selectedUser = googleAdsData.users?.find(u => u.userId === selectedUserId)

    if (!selectedUser) {
      // User not found - return empty structure
      return {
        team: {
          totalMinutes: 0,
          totalSessions: 0,
        },
        users: [],
      }
    }

    // Return filtered data with team summary matching just this user
    return {
      team: {
        totalMinutes: selectedUser.totalMinutes,
        totalSessions: selectedUser.sessions,
      },
      users: [selectedUser],
    }
  }, [selectedUserId, googleAdsData])

  // Filter Google Ads by Account data by selected user
  const filteredGoogleAdsByAccountData = useMemo(() => {
    if (!googleAdsByAccountData?.accounts) return null

    if (!selectedUserId) {
      // No filter - return all data
      return googleAdsByAccountData
    }

    // Filter accounts to only show those where the selected user worked
    const filteredAccounts = googleAdsByAccountData.accounts.filter(account =>
      account.userIds?.includes(selectedUserId)
    )

    return {
      accounts: filteredAccounts,
    }
  }, [selectedUserId, googleAdsByAccountData])
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Failed to load activity data</Typography>
      </Box>
    )
  }

  if (!data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">No activity data available</Typography>
      </Box>
    )
  }

  // Calculate max duration for progress bars using filtered data
  const maxUserDuration = Math.max(...filteredData.userBreakdown.map(u => u.duration), 1)
  const maxAreaDuration = Math.max(...filteredData.areaBreakdown.map(a => a.duration), 1)

  return (
    <ScrollableContainer
      sx={{
        p: 3,
        transition: 'padding 0.3s ease',
        pt: pinned ? '70px' : 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Activity Report
          </Typography>
          {selectedUserId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
              <Typography variant="body2" color="primary">
                Filtered by team member
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Team Member Filter */}
          <SelectActiveUser
            userOptions={userOptions}
            selectedUserOpt={userOptions.find(u => u.id === selectedUserId) || null}
            isUsersLoading={false}
            setUserDash={setSelectedUserId}
          />

          {/* Time Range Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select value={timeRange} label="Time Range" onChange={e => setTimeRange(e.target.value as TimeRange)}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="week">Last 7 Days</MenuItem>
              <MenuItem value="month">Last 30 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Google Ads API Error Banner */}
      {googleAdsError && (
        <Alert
          severity={googleAdsAuthError ? 'warning' : 'info'}
          sx={{ mb: 3 }}
          action={
            googleAdsAuthError ? (
              <Button color="inherit" size="small" onClick={startGoogleAdsReAuth} disabled={googleAdsAuthWindowOpen}>
                {googleAdsAuthWindowOpen ? 'Authenticating...' : 'Re-authenticate'}
              </Button>
            ) : undefined
          }
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {googleAdsErrorMessage || 'Google Ads API error'}
            </Typography>
            {googleAdsErrorDetails?.length > 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
                {googleAdsErrorDetails.join(' • ')}
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="body2" color="textSecondary">
                  Total Time
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {filteredData.summary.totalDurationFormatted}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon sx={{ color: theme.palette.success.main }} />
                <Typography variant="body2" color="textSecondary">
                  Page Visits
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {filteredData.summary.totalVisits}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon sx={{ color: theme.palette.info.main }} />
                <Typography variant="body2" color="textSecondary">
                  Active Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {filteredData.summary.uniqueUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BarChartIcon sx={{ color: theme.palette.warning.main }} />
                <Typography variant="body2" color="textSecondary">
                  Avg per Visit
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {filteredData.summary.averageDurationPerVisitFormatted}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* User Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Time by User
            </Typography>
            {filteredData.userBreakdown.length === 0 ? (
              <Typography color="textSecondary">No user data</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredData.userBreakdown.slice(0, 10).map(user => (
                  <Box key={user.userId}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.userName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={`${user.visits} visits`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60, textAlign: 'right' }}>
                          {user.durationFormatted}
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(user.duration / maxUserDuration) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: theme.palette.primary.main,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Area Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Time by Area
            </Typography>
            {filteredData.areaBreakdown.length === 0 ? (
              <Typography color="textSecondary">No area data</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredData.areaBreakdown.slice(0, 10).map(area => (
                  <Box key={area.area}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {area.area}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={`${area.visits} visits`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60, textAlign: 'right' }}>
                          {area.durationFormatted}
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(area.duration / maxAreaDuration) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: theme.palette.success.main,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Top Entities Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Most Viewed Entities
            </Typography>
            {filteredData.topEntities.length === 0 ? (
              <Typography color="textSecondary">No entity data</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Area</TableCell>
                      <TableCell align="left">Visits</TableCell>
                      <TableCell>Visitors</TableCell>
                      <TableCell align="left">Total Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.topEntities.slice(0, 15).map((entity, idx) => (
                      <TableRow key={`${entity.entityId}-${idx}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'left' }}>
                            {entity.entityName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                            <Chip
                              label={entity.area}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="left" sx={{ textAlign: 'left' }}>
                          {entity.visits}
                        </TableCell>
                        <TableCell>
                          <VisitorAvatars
                            visitors={entity.visitors}
                            userLookup={userLookup}
                            max={4}
                            fallbackCount={entity.uniqueUsers}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                            {entity.durationFormatted}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Organization Management Time */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BusinessIcon sx={{ color: theme.palette.secondary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Organization Management Time
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Total time spent managing each organization (profile + deals + tasks)
            </Typography>
            {orgTimeLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : !filteredOrgTimeData || filteredOrgTimeData.length === 0 ? (
              <Typography color="textSecondary">
                {selectedUserId ? 'No organization activity for this user' : 'No organization activity data'}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organization</TableCell>
                      <TableCell align="left">Profile</TableCell>
                      <TableCell align="left">Deals</TableCell>
                      <TableCell align="left">Tasks</TableCell>
                      <TableCell align="left">Total</TableCell>
                      <TableCell align="left">Visits</TableCell>
                      <TableCell>Team</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrgTimeData.slice(0, 15).map((org, idx) => (
                      <TableRow key={`${org.orgId}-${idx}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'left' }}>
                            {orgNameLookup[org.orgId] || org.orgId}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2" sx={{ textAlign: 'left' }}>
                            {org.directTimeFormatted}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2" sx={{ textAlign: 'left' }}>
                            {org.dealTimeFormatted}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Tooltip
                            title={
                              org.taskStatusBreakdown && org.taskStatusBreakdown.length > 0 ? (
                                <Box sx={{ p: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, display: 'block', mb: 0.5, textAlign: 'left' }}
                                  >
                                    Time by Task Status:
                                  </Typography>
                                  {org.taskStatusBreakdown.map(item => (
                                    <Box
                                      key={item.status}
                                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                                    >
                                      <Typography variant="caption" sx={{ textAlign: 'left' }}>
                                        {item.status}:
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'left' }}>
                                        {item.durationFormatted}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                'No task data'
                              )
                            }
                            arrow
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                cursor: org.taskTime > 0 ? 'help' : 'default',
                                textDecoration: org.taskTime > 0 ? 'underline dotted' : 'none',
                                textAlign: 'left',
                              }}
                            >
                              {org.taskTimeFormatted}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                            {org.totalTimeFormatted}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                            {org.visits > 0 && (
                              <Chip label={`${org.visits} org`} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                            )}
                            {org.dealVisits > 0 && (
                              <Chip
                                label={`${org.dealVisits} deal`}
                                size="small"
                                sx={{ height: 18, fontSize: '0.6rem' }}
                              />
                            )}
                            {org.taskVisits > 0 && (
                              <Chip
                                label={`${org.taskVisits} task`}
                                size="small"
                                sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha(theme.palette.warning.main, 0.1) }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <VisitorAvatars
                            visitors={org.visitors}
                            userLookup={userLookup}
                            max={4}
                            fallbackCount={org.uniqueUsers}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Google Ads Activity Tracking */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CampaignIcon sx={{ color: '#4285F4' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Google Ads Activity
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Time spent in Google Ads tracked via browser extension
            </Typography>
            {googleAdsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : !filteredGoogleAdsData?.users ||
              filteredGoogleAdsData.users.filter(u => u.sessions > 0).length === 0 ? (
              <Typography color="textSecondary">
                {selectedUserId
                  ? 'No Google Ads activity for this user'
                  : 'No Google Ads activity data. Install the Grobotic Activity Tracker extension to track time in Google Ads.'}
              </Typography>
            ) : (
              <>
                {/* Team Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="body2" color="textSecondary">
                          {selectedUserId ? 'User Time' : 'Total Time'}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#4285F4' }}>
                          {formatMinutes(filteredGoogleAdsData.team.totalMinutes)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="body2" color="textSecondary">
                          {selectedUserId ? 'User Sessions' : 'Total Sessions'}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#4285F4' }}>
                          {filteredGoogleAdsData.team.totalSessions}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="body2" color="textSecondary">
                          Active Users
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#4285F4' }}>
                          {filteredGoogleAdsData.users.filter(u => u.sessions > 0).length}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* User Breakdown Table */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell align="left">Sessions</TableCell>
                        <TableCell align="left">Time</TableCell>
                        <TableCell align="left">Avg per Session</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredGoogleAdsData.users
                        .filter(u => (selectedUserId ? true : u.sessions > 0)) // Show selected user even with 0 sessions
                        .sort((a, b) => b.totalMinutes - a.totalMinutes)
                        .map((user: GoogleAdsTeamSummaryUser) => {
                          const userInfo = userLookup[user.userId]
                          const avgPerSession = user.sessions > 0 ? Math.round(user.totalMinutes / user.sessions) : 0
                          return (
                            <TableRow key={user.userId}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <EntityAvatar
                                    name={userInfo?.name || user.userId}
                                    photoUrl={userInfo?.photoUrl}
                                    size="xs"
                                    showName={false}
                                    userId={user.userId}
                                    enableNav={true}
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {userInfo?.name || user.userId}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="left">
                                <Chip label={user.sessions} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                              </TableCell>
                              <TableCell align="left">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatMinutes(user.totalMinutes)}
                                </Typography>
                              </TableCell>
                              <TableCell align="left">
                                <Typography variant="body2" color="textSecondary">
                                  {formatMinutes(avgPerSession)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </Grid>

        {/* Google Ads Time by Organization / Account */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', gap: 1, mb: 2 }}>
              <BusinessIcon sx={{ color: '#4285F4' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Google Ads Time by Organization
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Time spent on each Google Ads account, mapped to organizations (set googleAdsCustomerId on organization to
              match the ID captured from URLs)
            </Typography>
            {googleAdsByAccountLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : !filteredGoogleAdsByAccountData?.accounts || filteredGoogleAdsByAccountData.accounts.length === 0 ? (
              <Typography color="textSecondary">
                {selectedUserId
                  ? 'No Google Ads account activity for this user'
                  : 'No account-level tracking data available yet. Time will appear here once sessions are tracked with account IDs.'}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organization / Account</TableCell>
                      <TableCell>Google Ads ID</TableCell>
                      <TableCell>Sessions</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Team</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredGoogleAdsByAccountData.accounts.map((account: GoogleAdsAccountBreakdown) => (
                      <TableRow key={account.accountId}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {account.orgName ? (
                              <>
                                <BusinessIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {account.orgName}
                                </Typography>
                              </>
                            ) : (
                              <UnlinkedAccount googleAdsAccountsById={googleAdsAccountsById} account={account} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              color: 'text.secondary',
                              textAlign: 'left',
                            }}
                          >
                            {account.accountId}
                          </Typography>
                        </TableCell>
                        <TableCell align="left" sx={{ textAlign: 'left' }}>
                          <Chip label={account.sessions} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell align="left">
                          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                            {formatMinutes(account.totalMinutes)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            {account.userIds.slice(0, 4).map(userId => {
                              const userInfo = userLookup[userId]
                              return (
                                <Tooltip key={userId} title={userInfo?.name || userId} arrow>
                                  <span>
                                    <EntityAvatar
                                      name={userInfo?.name || userId}
                                      photoUrl={userInfo?.photoUrl}
                                      size="xs"
                                      showName={false}
                                      userId={userId}
                                      enableNav={true}
                                    />
                                  </span>
                                </Tooltip>
                              )
                            })}
                            {account.userIds.length > 4 && (
                              <Tooltip title={`+${account.userIds.length - 4} more`} arrow>
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    bgcolor: 'action.selected',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.6rem',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                  }}
                                >
                                  +{account.userIds.length - 4}
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {googleAdsByAccountData?.totals && googleAdsByAccountData.totals.accountsTracked > 0 && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`${googleAdsByAccountData.totals.accountsWithOrg} of ${googleAdsByAccountData.totals.accountsTracked} accounts linked to orgs`}
                  size="small"
                  color={
                    googleAdsByAccountData.totals.accountsWithOrg === googleAdsByAccountData.totals.accountsTracked
                      ? 'success'
                      : 'warning'
                  }
                  variant="outlined"
                />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </ScrollableContainer>
  )
}

export default ActivityReport
